import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, LogOut, Menu, X } from 'lucide-react';
import { TimeWindowSelector, loadStoredWindow, saveStoredWindow, type TimeWindow } from './TimeWindowSelector';
import { useAdminHub } from '../contexts/AdminHubContext';
import { getAlerts } from '../api/orchestratorClient';

export { loadStoredWindow, saveStoredWindow };
export type { TimeWindow };

interface HeaderProps {
  isDevMode: boolean;
  connectionStatus?: 'Connected' | 'Connecting' | 'Disconnected';
  onSignOut?: () => void;
  signOutError?: string | null;
  onDismissSignOutError?: () => void;
  onToggleNav?: () => void;
  navOpen?: boolean;
}

/**
 * Active-alert badge (US-24-GR-27) — fetches the open alert count on mount,
 * then increments live as SignalR "ThresholdAlert" events arrive on the
 * Admin_Orchestrator group. Clicking navigates to /queues, the alerts' home.
 * Subtle (hidden) at zero so it never competes for attention when healthy.
 */
function AlertBadge() {
  const navigate = useNavigate();
  const { events } = useAdminHub();
  const [baseCount, setBaseCount] = useState<number | null>(null);
  const seenEventCountRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    // isOpen server-side filter + `total` from the paged envelope — pageSize:1
    // is enough since we only need the count, not the items. Avoids both an
    // arbitrary page-100 client-side counting cap and counting closed alerts.
    getAlerts({ isOpen: true, pageSize: 1 })
      .then((res) => {
        if (cancelled) return;
        setBaseCount(res.total);
      })
      .catch(() => {
        if (!cancelled) setBaseCount(0);
      });
    return () => { cancelled = true; };
  }, []);

  // Count new ThresholdAlert events since baseCount was fetched. The events
  // buffer is newest-first and grows monotonically per hook, so counting new
  // arrivals (not just current-buffer matches) avoids double counting on
  // effect re-runs.
  const [liveIncrement, setLiveIncrement] = useState(0);
  useEffect(() => {
    const newEvents = events.slice(0, events.length - seenEventCountRef.current);
    const newAlerts = newEvents.filter((e) => e.EventType === 'ThresholdAlert').length;
    if (newAlerts > 0) {
      setLiveIncrement((prev) => prev + newAlerts);
    }
    seenEventCountRef.current = events.length;
  }, [events]);

  const count = (baseCount ?? 0) + liveIncrement;

  if (baseCount === null || count === 0) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => navigate('/queues')}
      aria-label={`${count} active alert${count === 1 ? '' : 's'} — view queue health`}
      className="relative flex items-center gap-1.5 px-2 py-1 rounded-lg text-red-300 hover:bg-red-500/10 transition-colors min-h-[48px]"
    >
      <Bell className="w-4 h-4" aria-hidden="true" />
      <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-xs font-semibold">
        {count > 99 ? '99+' : count}
      </span>
    </button>
  );
}

export function Header({
  isDevMode,
  connectionStatus = 'Disconnected',
  onSignOut,
  signOutError,
  onDismissSignOutError,
  onToggleNav,
  navOpen = false,
}: HeaderProps) {
  const [timeWindow, setTimeWindow] = useState<TimeWindow>(loadStoredWindow);

  function handleTimeWindowChange(w: TimeWindow) {
    setTimeWindow(w);
    saveStoredWindow(w);
  }

  const statusColor =
    connectionStatus === 'Connected'
      ? 'bg-emerald-500'
      : connectionStatus === 'Connecting'
      ? 'bg-yellow-500'
      : 'bg-zinc-600';

  return (
    <>
      <header className="flex items-center gap-2 min-h-14 px-3 md:px-6 bg-zinc-950 border-b border-zinc-800">
        {onToggleNav && (
          <button
            type="button"
            onClick={onToggleNav}
            aria-label={navOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={navOpen}
            aria-controls="main-navigation"
            className="md:hidden shrink-0 flex items-center justify-center min-w-[48px] min-h-[48px] rounded-lg text-zinc-300 hover:bg-zinc-800 transition-colors"
          >
            {navOpen ? (
              <X className="w-5 h-5" aria-hidden="true" />
            ) : (
              <Menu className="w-5 h-5" aria-hidden="true" />
            )}
          </button>
        )}
        {isDevMode && (
          <div
            role="alert"
            aria-live="polite"
            className="flex items-center gap-2 px-3 py-1 bg-yellow-500/20 border border-yellow-500/40 rounded text-yellow-300 text-xs font-medium"
          >
            <span aria-hidden="true">⚠</span>
            DevMode active — MSAL auth bypassed
          </div>
        )}

        <div className="flex items-center gap-2 md:gap-4 ml-auto min-w-0">
          <AlertBadge />
          <div className="hidden sm:block">
            <TimeWindowSelector value={timeWindow} onChange={handleTimeWindowChange} />
          </div>

          <div className="flex items-center gap-2" aria-label={`Connection status: ${connectionStatus}`}>
            <span
              aria-hidden="true"
              className={`inline-block w-2 h-2 rounded-full ${statusColor}`}
            />
            <span className="hidden sm:inline text-xs text-zinc-400">{connectionStatus}</span>
          </div>

          {onSignOut && (
            <button
              type="button"
              onClick={onSignOut}
              className="shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-lg text-sm text-zinc-300 hover:bg-zinc-800 transition-colors min-h-[48px]"
            >
              <LogOut className="w-4 h-4" aria-hidden="true" />
              Sign out
            </button>
          )}
        </div>
      </header>
      {signOutError && (
        <div
          role="alert"
          className="flex items-center justify-between gap-3 px-3 md:px-6 py-2 bg-red-500/10 border-b border-red-500/30 text-sm text-red-300"
        >
          <span>{signOutError}</span>
          {onDismissSignOutError && (
            <button
              type="button"
              onClick={onDismissSignOutError}
              aria-label="Dismiss sign out error"
              className="shrink-0 flex items-center justify-center min-w-[48px] min-h-[48px] rounded-lg hover:bg-red-500/20 transition-colors"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          )}
        </div>
      )}
    </>
  );
}
